import React, { useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import Spinner from "../../../components/spinner";
import MuiAlert from "@mui/material/Alert";
import Modal from "@mui/material/Modal";
import { GrAdd } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import useCreate from "../../../hooks/useCreate";
import useUploadMedia from "../../../hooks/file/useUploadMedia";
import Loader from "../../../components/loader";
import toast from "react-hot-toast";
import Editor from "../../../components/elements/Editor";
import { useForm, useFieldArray } from "react-hook-form";

// const fileTypes = ["JPG", "PNG", "JPEG"]; 
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AddProduct() {
  const navigate = useNavigate();

  const defaultValues = {
    name: "",
    title: "",
    slug: "",
    overview: "",
    banner: "",
    video: "",
    logo: "",
    // secondary_img: "",
    thumbnail: "",
    client: "",
    genre: "",
    services: "",
    platform: "",
    product_links: [{ name: "", link: "" }] // Added product_links array
  };

  const fileInputRef = useRef(null);
  const logoFileInputRef = useRef(null); // Ref for logo file input
  // const secondaryImageFileInputRef = useRef(null); // Ref for secondary image file input
  const thumbnailFileInputRef = useRef(null); // Ref for thumbnail file input
  const [imagePreview, setImagePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null); // Preview for logo
  // const [secondaryImagePreview, setSecondaryImagePreview] = useState(null); // Preview for secondary image
  const [thumbnailPreview, setThumbnailPreview] = useState(null); // Preview for thumbnail
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control, // Added control for useFieldArray
    formState: { errors },
  } = useForm({ defaultValues });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "product_links"
  });

  // Watch overview and banner fields for dynamic updates/validation
  const watchedOverview = watch("overview");
  const watchedBanner = watch("banner");
  const watchedLogo = watch("logo"); // Watch logo for validation
  // const watchedSecondaryImg = watch("secondary_img"); // Watch secondary image for validation
  const thumbnail = watch("thumbnail"); // Watch thumbnail for validation
  const watchedTitle = watch("title"); // Watch title for slug generation

  // Register overview field manually for validation
  useEffect(() => {
    register("overview", { required: "Overview is required" });
    register("banner"); // Make banner optional
    register("logo"); // Register logo field
    // register("secondary_img"); // Register secondary image field
    register("thumbnail"); // Register thumbnail field
  }, [register]);

  // Add generate slug function
  const generateSlug = () => {
    if (watchedTitle) {
      const slug = watchedTitle
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen

      setValue("slug", slug);
    }
  };

  // Update overview value in form state when Editor changes
  const handleChangeEditor = (newvalue) => {
    setValue("overview", newvalue, { shouldValidate: true }); // Update and validate
  };

  const closeErrorMessage = () => {
    setIsError(false);
  };
  const { setDataToServer } = useCreate({
    url: "create-product",
    refreshUrl: "get-product-list",
    onSuccess: () => {
      toast.success("Product Added Successfully", {
        position: "top-right",
        duration: 3000,
      });
      // Navigate to products listing page after successful creation
      navigate('/catalog/products');

      // Still reset the form in case navigation is delayed
      reset(defaultValues);
      setImagePreview(null);
      setIsSuccess(true);
      setIsError(false);
    },
    onError: (err) => {
      setIsError(true);
      setIsSuccess(false);
      toast.error(`Error: ${err?.message || 'Failed to add product'}`, {
        position: "top-right",
        duration: 3000,
      });
    }
  });

  // Handle form submission
  const onFormSubmit = (data) => {
    // Filter out empty product links
    if (data.product_links) {
      data.product_links = data.product_links.filter(
        link => link.name.trim() !== "" || link.link.trim() !== ""
      );
    }
    setDataToServer.mutate(data);
  };

  const handleCloseAndReset = () => {
    reset(defaultValues); // Reset form using react-hook-form's reset
    setImagePreview(null); // Reset image preview
    closeErrorMessage();
    setIsSuccess(false);
  };
  useEffect(() => {
    isError && setTimeout(() => setIsError(false), [4000]);
    isSuccess && setTimeout(() => setIsSuccess(false), [4000]); // Auto-hide success message
  }, [isError, isSuccess]);

  const handleOpenFileUploadDialog = () => {
    fileInputRef.current.click();
  };
  const handleOpenLogoUploadDialog = () => {
    logoFileInputRef.current.click();
  };
  // const handleOpenSecondaryImageUploadDialog = () => {
  //   secondaryImageFileInputRef.current.click();
  // };
  const handleOpenThumbnailUploadDialog = () => {
    thumbnailFileInputRef.current.click();
  };


  const { upload } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      // Access properties directly from the response object
      console.log("res", res);

      // Direct access to properties (not nested in data)
      const imagePath = res?.image_path;
      const documentId = res?.document_id;

      if (imagePath) {
        // Update the banner field in react-hook-form state
        setValue("banner", imagePath, { shouldValidate: true }); // Update and validate
        setImagePreview(imagePath); // Update the visual preview
        toast.success("Banner Image Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        toast.error("Failed to get image path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        // Optionally clear the banner value if upload failed to provide URL
        setValue("banner", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Image Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      // Optionally clear the banner value on error
      setValue("banner", "", { shouldValidate: true });
    }
  });

  // Logo upload handler (same as banner)
  const { upload: uploadLogo } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      const imagePath = res?.image_path;
      if (imagePath) {
        setValue("logo", imagePath, { shouldValidate: true });
        setLogoPreview(imagePath);
        toast.success("Logo Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        toast.error("Failed to get logo path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        setValue("logo", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Logo Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setValue("logo", "", { shouldValidate: true });
    }
  });

  const { upload: uploadThumbnail } = useUploadMedia({
    url: "upload-document",
    onSuccess: (res) => {
      const imagePath = res?.image_path;
      if (imagePath) {
        setValue("thumbnail", imagePath, { shouldValidate: true });
        setThumbnailPreview(imagePath);
        toast.success("Thumbnail Uploaded Successfully", {
          position: "top-right",
          duration: 3000,
        });
      } else {
        toast.error("Failed to get thumbnail path from upload response.", {
          position: "top-right",
          duration: 3000,
        });
        setValue("thumbnail", "", { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(`Thumbnail Upload Failed: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
      setValue("thumbnail", "", { shouldValidate: true });
    }
  });

  // const { upload: uploadSecondaryImg } = useUploadMedia({
  //   url: "upload-document",
  //   onSuccess: (res) => {
  //     const imagePath = res?.image_path;
  //     if (imagePath) {
  //       setValue("secondary_img", imagePath, { shouldValidate: true });
  //       setSecondaryImagePreview(imagePath);
  //       toast.success("Secondary Image Uploaded Successfully", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //     } else {
  //       toast.error("Failed to get secondary image path from upload response.", {
  //         position: "top-right",
  //         duration: 3000,
  //       });
  //       setValue("secondary_img", "", { shouldValidate: true });
  //     }
  //   },
  //   onError: (err) => {
  //     toast.error(`Secondary Image Upload Failed: ${err?.message || 'Unknown error'}`, {
  //       position: "top-right",
  //       duration: 3000,
  //     });
  //     setValue("secondary_img", "", { shouldValidate: true });
  //   }
  // });  

  const handleUploadFile = (e) => {
    let bannerFile = e.target?.files[0];
    if (!bannerFile) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (!allowedTypes.includes(bannerFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null; // Clear the file input
      return;
    }

    let data = {
      file: bannerFile,
      type: 'products'
    };
    upload.mutate(data);
    e.target.value = null; // Clear the file input after selection
  };

  const handleDeleteBanner = () => {
    setValue("banner", "", { shouldValidate: true });
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = () => {
    setValue("logo", "", { shouldValidate: true });
    setLogoPreview(null);
    if (logoFileInputRef.current) {
      logoFileInputRef.current.value = "";
    }
  };

  const handleDeleteThumbnail = () => {
    setValue("thumbnail", "", { shouldValidate: true });
    setThumbnailPreview(null);
    if (thumbnailFileInputRef.current) {
      thumbnailFileInputRef.current.value = "";
    }
  };

  // Logo file upload
  const handleUploadLogoFile = (e) => {
    let logoFile = e.target?.files[0];
    if (!logoFile) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(logoFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null;
      return;
    }
    let data = {
      file: logoFile,
      type: 'products'
    };
    uploadLogo.mutate(data);
    e.target.value = null;
  };

  const handleUploadThumbnailFile = (e) => {
    let thumbnailFile = e.target?.files[0];
    if (!thumbnailFile) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(thumbnailFile.type)) {
      toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
      e.target.value = null;
      return;
    }
    let data = {
      file: thumbnailFile,
      type: 'products'
    };
    uploadThumbnail.mutate(data);
    e.target.value = null;
  };

  // const handleUploadSecondaryImageFile = (e) => {
  //   let secondaryImageFile = e.target?.files[0];
  //   if (!secondaryImageFile) return;
  //   const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
  //   if (!allowedTypes.includes(secondaryImageFile.type)) {
  //     toast.error("Invalid file type. Please upload JPG, JPEG, or PNG.", { duration: 3000 });
  //     e.target.value = null;
  //     return;
  //   }
  //   let data = {
  //     file: secondaryImageFile,
  //     type: 'products'
  //   };
  //   uploadSecondaryImg.mutate(data);
  //   e.target.value = null;
  // };

  return (
    <>
      {(upload.isLoading || setDataToServer.isLoading) && (
        <div className="fixed w-full h-full bg-[#ffffff70] top-0 left-0 z-50">
          <div className="flex w-full items-center justify-center h-full">
            <Loader className={"text-black !w-12 !h-12"} />
          </div>
        </div>
      )}
      <div className="container_xxl bg-[#eceff1] px-4">
        <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-10 bg-[#eceff1]">
          <div>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          </div>
          <div>
            <h1 className="heading">Add Project</h1>
          </div>
        </div>
        <div className="form-body py-8">
          {/* Use handleSubmit from react-hook-form */}
          <form className="" autoComplete="off" onSubmit={handleSubmit(onFormSubmit)}>
            {/* New Form Structure */}
            <div className="grid grid-cols-1 gap-4">
              <div className="mb-3 flex items-center justify-between border-b border-gray-400">
                <h1 className="font-semibold text-2xl pb-3">Project Information</h1>
              </div>
              <div>
                <label htmlFor="name" className="label">
                  Name <small className="text-red-700">*</small>
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Project Name"
                  className={`input ${errors.name ? 'border-red-500' : ''}`}
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>}
              </div>
              <div>
                <label htmlFor="title" className="label">
                  Title <small className="text-red-700">*</small>
                </label>
                <input
                  id="title"
                  type="text"
                  placeholder="Project Title"
                  className={`input ${errors.title ? 'border-red-500' : ''}`}
                  {...register("title", {
                    required: "Title is required",
                    onBlur: generateSlug
                  })}
                />
                {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title.message}</p>}
              </div>

              {/* Slug Field */}
              <div>
                <label htmlFor="slug" className="label">
                  Slug <small className="text-red-700">*</small>
                </label>
                <input
                  id="slug"
                  type="text"
                  placeholder="project-slug"
                  className={`input ${errors.slug ? 'border-red-500' : ''}`}
                  {...register("slug", { required: "Slug is required" })}
                />
                {errors.slug && <p className="text-red-600 text-sm mt-1">{errors.slug.message}</p>}
              </div>

              {/* Client Field */}
              <div>
                <label htmlFor="client" className="label">
                  Client
                </label>
                <input
                  id="client"
                  type="text"
                  placeholder="Client Name"
                  className={`input ${errors.client ? 'border-red-500' : ''}`}
                  {...register("client")}
                />
                {errors.client && <p className="text-red-600 text-sm mt-1">{errors.client.message}</p>}
              </div>

              {/* Genre Field */}
              <div>
                <label htmlFor="genre" className="label">
                  Genre
                </label>
                <input
                  id="genre"
                  type="text"
                  placeholder="Genre"
                  className={`input ${errors.genre ? 'border-red-500' : ''}`}
                  {...register("genre")}
                />
                {errors.genre && <p className="text-red-600 text-sm mt-1">{errors.genre.message}</p>}
              </div>

              {/* Services Field */}
              <div>
                <label htmlFor="services" className="label">
                  Services
                </label>
                <input
                  id="services"
                  type="text"
                  placeholder="Services Provided"
                  className={`input ${errors.services ? 'border-red-500' : ''}`}
                  {...register("services")}
                />
                {errors.services && <p className="text-red-600 text-sm mt-1">{errors.services.message}</p>}
              </div>

              {/* Platform Field */}
              <div>
                <label htmlFor="platform" className="label">
                  Platform
                </label>
                <input
                  id="platform"
                  type="text"
                  placeholder="Platform"
                  className={`input ${errors.platform ? 'border-red-500' : ''}`}
                  {...register("platform")}
                />
                {errors.platform && <p className="text-red-600 text-sm mt-1">{errors.platform.message}</p>}
              </div>

              {/* Links Field */}
              {/* Product Links Section */}
              <div className="border border-gray-300 p-4 rounded-md">
                <div className="flex justify-between items-center mb-4">
                  <label className="label mb-0">Product Links (Max 3)</label>
                  {fields.length < 3 && (
                    <Button
                      variant="outlined"
                      startIcon={<GrAdd />}
                      size="small"
                      onClick={() => append({ name: "", link: "" })}
                    >
                      Add Link
                    </Button>
                  )}
                </div>

                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-4 items-start mb-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Label (e.g. Learn More)"
                        className={`input ${errors.product_links?.[index]?.name ? 'border-red-500' : ''}`}
                        {...register(`product_links.${index}.name`, {
                          validate: (value) => {
                            const linkValue = watch(`product_links.${index}.link`);
                            if (linkValue && !value) return "Label is required if link is provided";
                            return true;
                          }
                        })}
                      />
                      {errors.product_links?.[index]?.name && (
                        <p className="text-red-600 text-sm mt-1">{errors.product_links[index].name.message}</p>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Link URL (e.g. https://example.com)"
                        className={`input ${errors.product_links?.[index]?.link ? 'border-red-500' : ''}`}
                        {...register(`product_links.${index}.link`, {
                          validate: (value) => {
                            const labelValue = watch(`product_links.${index}.name`);
                            if (labelValue && !value) return "Link is required if label is provided";
                            return true;
                          }
                        })}
                      />
                      {errors.product_links?.[index]?.link && (
                        <p className="text-red-600 text-sm mt-1">{errors.product_links[index].link.message}</p>
                      )}
                    </div>
                    <IconButton onClick={() => remove(index)} color="error" className="mt-1">
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))}
              </div>

              <div>
                <label htmlFor="overview" className="label">
                  Overview <small className="text-red-700">*</small>
                </label>
                <Editor
                  value={watchedOverview}
                  onChange={handleChangeEditor}
                />
                {/* Display error for overview */}
                {errors.overview && <p className="text-red-600 text-sm mt-1">{errors.overview.message}</p>}
              </div>
              <div>
                <label htmlFor="video" className="label">
                  Video URL
                </label>
                <input
                  id="video"
                  type="url" // Use type="url" for better validation hint
                  placeholder="Paste Video URL (e.g., YouTube, Vimeo)"
                  className={`input ${errors.video ? 'border-red-500' : ''}`} // Add error styling
                  {...register("video", { // Register with URL pattern validation (optional)
                    pattern: {
                      value: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                      message: "Please enter a valid URL"
                    }
                  })}
                />
                {errors.video && <p className="text-red-600 text-sm mt-1">{errors.video.message}</p>}
              </div>
              {/* Banner Upload Section */}
              <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Banner Image
                    </h1>
                    <small className="text-blue-700">
                      Note: Recommended Size: 1920 x 1080 px (JPG, PNG, JPEG)
                      <br />
                      Keep the focal point of the artwork in the top-middle. Avoid putting important details at the very bottom, as they will be covered by the black info bar.
                    </small>
                  </div>
                </div>
                <div className="">
                  {upload.isLoading ? (
                    <>
                      <Spinner />
                    </>
                  ) : (
                    <>
                      {imagePreview && (
                        <div className="my-4">
                          <img
                            src={imagePreview}
                            alt="Banner preview"
                            className="object-contain w-48 h-48 border mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleOpenFileUploadDialog}
                              disabled={upload.isLoading}
                            >
                              Change Image
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={handleDeleteBanner}
                              startIcon={<DeleteIcon />}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {/* Display error for banner */}
                  {errors.banner && <p className="text-red-600 text-sm mt-1">{errors.banner.message}</p>}

                  <div className="mt-5">
                    {!imagePreview && (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenFileUploadDialog}
                        disabled={upload.isLoading}
                      >
                        Add Banner Image
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadFile}
                      accept="image/jpeg, image/png, image/jpg"
                    />
                  </div>
                </div>
              </div>
              {/* Logo Upload Section */}
              <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Logo
                    </h1>
                    <small className="text-blue-700">
                      Note: Recommended Size: 500 px width (Transparent PNG)
                    </small>
                  </div>
                </div>
                <div className="">
                  {/* <label htmlFor="" className="label">
                    Logo
                  </label> */}
                  {uploadLogo.isLoading ? (
                    <>
                      <Spinner />
                    </>
                  ) : (
                    <>
                      {logoPreview && (
                        <div className="my-4">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="object-contain w-48 h-48 border mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleOpenLogoUploadDialog}
                              disabled={uploadLogo.isLoading}
                            >
                              Change Logo
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={handleDeleteLogo}
                              startIcon={<DeleteIcon />}
                            >
                              Remove Logo
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {errors.logo && !logoPreview && <p className="text-red-600 text-sm mt-1">{errors.logo.message}</p>}
                  <div className="mt-5">
                    {!logoPreview && (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenLogoUploadDialog}
                        disabled={uploadLogo.isLoading}
                      >
                        Add Logo
                      </Button>
                    )}
                    <input
                      ref={logoFileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadLogoFile}
                      accept="image/jpeg, image/png, image/jpg"
                    />
                  </div>
                </div>
              </div>

              {/* Secondary Image Section - Commented out */}
              {/* <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Secondary Image
                    </h1>
                    <small className="text-red-700">
                      Note: Image to be less than 1mb (JPG, PNG, JPEG)
                    </small>
                  </div>
                </div>
                <div className="">
                  <label htmlFor="" className="label">
                    Secondary Image
                  </label>
                  {uploadSecondaryImg.isLoading ? (
                    <>
                      <Spinner />
                    </>
                  ) : (
                    <>
                      {secondaryImagePreview && (
                        <img
                          src={secondaryImagePreview}
                          alt="Secondary Image preview"
                          className="object-contain w-48 h-48 my-4 border"
                        />
                      )}
                    </>
                  )}
                  {errors.secondary_img && !secondaryImagePreview && <p className="text-red-600 text-sm mt-1">{errors.secondary_img.message}</p>}
                  <div className="mt-5">
                    <Button
                      type="button"
                      variant="contained"
                      color="success"
                      className="primaryBtn-contained"
                      onClick={handleOpenSecondaryImageUploadDialog}
                      disabled={uploadSecondaryImg.isLoading}
                    >
                      {secondaryImagePreview
                        ? "Update Secondary Image"
                        : "Add Secondary Image"}
                    </Button>
                    <input
                      ref={secondaryImageFileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadSecondaryImageFile}
                      accept="image/jpeg, image/png, image/jpg"
                    />
                  </div>
                </div>
              </div> */}

              {/* Thumbnail Upload Section */}
              <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Thumbnail Image
                    </h1>
                    <small className="text-blue-700">
                      Note: Recommended Size: 1200 x 1600 px (JPG, PNG, JPEG)
                    </small>
                  </div>
                </div>
                <div className="">
                  {uploadThumbnail.isLoading ? (
                    <>
                      <Spinner />
                    </>
                  ) : (
                    <>
                      {thumbnailPreview && (
                        <div className="my-4">
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="object-contain w-48 h-48 border mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={handleOpenThumbnailUploadDialog}
                              disabled={uploadThumbnail.isLoading}
                            >
                              Change Image
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={handleDeleteThumbnail}
                              startIcon={<DeleteIcon />}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {errors.thumbnail && <p className="text-red-600 text-sm mt-1">{errors.thumbnail.message}</p>}
                  <div className="mt-5">
                    {!thumbnailPreview && (
                      <Button
                        type="button"
                        variant="contained"
                        color="success"
                        className="primaryBtn-contained"
                        onClick={handleOpenThumbnailUploadDialog}
                        disabled={uploadThumbnail.isLoading}
                      >
                        Add Thumbnail Image
                      </Button>
                    )}
                    <input
                      ref={thumbnailFileInputRef}
                      type="file"
                      className="hidden"
                      onChange={handleUploadThumbnailFile}
                      accept="image/jpeg, image/png, image/jpg"
                    />
                  </div>
                </div>
              </div>

            </div>

            <div className="text-center my-6">
              {setDataToServer.isLoading ? (
                <Spinner />
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  className="primaryBtn-contained"
                  disabled={upload.isLoading || setDataToServer.isLoading}
                >
                  Save Project
                </Button>
              )}
            </div>
          </form>
        </div >
        {/* Error Alert */}
        {
          isError && !setDataToServer.isLoading && (
            <Alert
              onClose={closeErrorMessage}
              className="mb-4"
              severity="error"
              sx={{ width: "100%" }}
            >
              {/* {setDataToServer?.error?.message}, Please try again later */}
              Failed to save project. Please check the details and try again.
            </Alert>
          )
        }
      </div >
    </>
  );
}

export default AddProduct;
